import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation
} from '@angular/core';

import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';

import {
  DeviceService, IconSizeEnum, ISdFormComponentItem, PositionEnum, SdAccordionComponent, SdAccordionGroupComponent,
  SdButtonComponent, SdFormRadioGroupComponent, SdHtmlComponent, SdThemeComponent, ThemesTextEnum
} from '@seb/web-design-system/core';

import type { ICategory, IQuestionSurvey } from './faq.interface';

@Component({
  selector: 'sd-faq',
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdAccordionGroupComponent,
    SdAccordionComponent,
    FormsModule,
    ReactiveFormsModule,
    SdFormRadioGroupComponent,
    SdButtonComponent,
    RouterLink,
    TranslateModule,
    SdHtmlComponent
  ]
})
export class SdFaqComponent extends SdThemeComponent implements OnDestroy, OnInit {
  /**
   * FAQ icon position
   * * @param {PositionEnum}
   */
  @Input()
  iconPos: PositionEnum.left | PositionEnum.right = PositionEnum.left;

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevelCategory = 3;

  /**
   * Categories of question: contain category title and each question/answer of the category
   * * @param {ICategory[]}
   */
  @Input()
  categories: ICategory[];

  /**
   * List of the reasons the FAQ answer is not useful ({label, value})
   * * @param {ISdFormComponentItem[]}
   */
  @Input()
  answerReasons: ISdFormComponentItem[];

  /**
   * Add a contact button or not
   */
  @Input()
  hasContactButton = false;

  /**
   * contactLink
   */
  @Input()
  contactLink: string;

  /*
   * Display or not the form to know if the answer is useful or not and why
   */
  @Input()
  hasForm = true;

  @Output()
  answerEmitter = new EventEmitter<IQuestionSurvey>();

  @Output()
  categoryOpened = new EventEmitter<string>();

  answersRadio: ISdFormComponentItem[];
  formUsefulAnswers: FormGroup = new FormGroup({});
  iconSize = IconSizeEnum.small;
  subscriptions = new Subscription();
  answerSent: { [id: string]: boolean } = {};
  isMobile$: boolean;
  themesTextEnum = ThemesTextEnum;

  constructor(
    private translateService: TranslateService,
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    for (const category of this.categories) {
      for (const question of category.questions) {
        this.formUsefulAnswers.addControl(
          'formAnswer' + question.id,
          new FormControl(''),
        );
        this.formUsefulAnswers.addControl(
          'formAnswerReason' + question.id,
          new FormControl(''),
        );
      }
    }

    this.subscriptions.add(
      this.translateService.get(['faq.yes', 'faq.no']).subscribe((results) => {
        this.answersRadio = [
          {
            libelle: results['faq.yes'],
            value: true
          },
          {
            libelle: results['faq.no'],
            value: false
          }
        ];
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile$ = x;
        this.cdRef.markForCheck();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onSubmit(event: MouseEvent, idQuestion: string) {
    if (this.formUsefulAnswers.value['formAnswer' + idQuestion].value) {
      this.answerEmitter.emit({
        isUseful: this.formUsefulAnswers.value['formAnswer' + idQuestion].value,
        questionId: idQuestion
      });
      event.stopPropagation();
      this.answerSent[idQuestion] = true;
    } else if (
      this.formUsefulAnswers.value['formAnswerReason' + idQuestion].value ||
      this.formUsefulAnswers.value['formAnswerReason' + idQuestion].value === 0
    ) {
      this.answerEmitter.emit({
        isUseful: this.formUsefulAnswers.value['formAnswer' + idQuestion].value,
        reason: this.formUsefulAnswers.value['formAnswerReason' + idQuestion].value,
        questionId: idQuestion
      });
      event.stopPropagation();
      this.answerSent[idQuestion] = true;
    }
  }

  onClickCategory(toggle: boolean, categoryName: string) {
    if (!toggle) {
      this.categoryOpened.emit(categoryName);
    }
  }
}
